import React from 'react';
import { Select } from 'antd';
import { F90M } from '../../../services/pw9208Api';
import { PW9208Names } from '../hooks/usePW9208Lookup';

const SEVERANCE_REASONS = [
  '轉業或轉讓',
  '虧損或業務緊縮',
  '不可抗力暫停工作在一個月以上',
  '事業單位改組或轉讓',
  '勞工對於所擔任之工作確不能勝任',
  '業務性質變更有減少勞工之必要又無適當工作可供安置',
];

interface Tab1Props {
  formData: Partial<F90M>;
  names: PW9208Names;
  isEditing: boolean;
  getField: <K extends keyof F90M>(key: K) => F90M[K];
  setField: (key: keyof F90M, value: unknown) => void;
  resolveSingleName: (nameKey: keyof PW9208Names, lookupType: string, code: string) => Promise<void>;
  getFieldError: (key: string) => string;
}

const Tab1BasicInfo: React.FC<Tab1Props> = React.memo(({
  names, isEditing, getField, setField, resolveSingleName, getFieldError,
}) => {
  const dateStr = (v: string | null | undefined): string => {
    if (!v) return '';
    return typeof v === 'string' ? v.replace('T', ' ').slice(0, 10) : '';
  };

  // BR-C01: Auto-calculate severance cost (f90m_18 = f90m_15 * f90m_16)
  // Only when f90m_18 is empty (0 or not set), to allow manual override.
  const autoCalcSeveranceCost = (seniority: number, salary: number) => {
    const currentCost = getField('f90m_18');
    if (!currentCost || currentCost === 0) {
      const cost = Math.round(seniority * salary);
      setField('f90m_18', cost);
    }
  };

  return (
    <div className="pw9208-tab1">
      {/* Top section: ID + employee + notice date + advance notice date */}
      <div className="form-grid">
        <label>資遣單號</label>
        <input
          value={getField('f90m_01') || ''}
          disabled
          className="field-readonly"
        />
        <label>通知日期</label>
        <input
          type="date"
          value={dateStr(getField('f90m_12'))}
          disabled={!isEditing}
          onChange={e => setField('f90m_12', e.target.value || null)}
          className={getFieldError('F90M_12') ? 'field-error' : ''}
        />
      </div>

      <div className="form-grid">
        <label><span className="required">*</span>員工代號</label>
        <div className="lookup-field">
          <input
            value={getField('f90m_11') || ''}
            disabled={!isEditing}
            maxLength={8}
            onChange={e => setField('f90m_11', e.target.value)}
            onBlur={() => resolveSingleName('employee', 'employee', getField('f90m_11') || '')}
            className={getFieldError('F90M_11') ? 'field-error' : ''}
          />
          <span className="lookup-name">{names.employee}</span>
        </div>
        <label>預告日期</label>
        <input
          type="date"
          value={dateStr(getField('f90m_17'))}
          disabled={!isEditing}
          onChange={e => setField('f90m_17', e.target.value || null)}
        />
      </div>

      {getFieldError('F90M_11') && (
        <div className="field-error-msg">{getFieldError('F90M_11')}</div>
      )}

      {/* Employee auto-filled fields */}
      <div className="form-grid">
        <label>部門代號</label>
        <div className="lookup-field">
          <input
            value={getField('f90m_21') || ''}
            disabled
            className="field-readonly"
          />
          <span className="lookup-name">{names.department}</span>
        </div>
        <label>職稱代號</label>
        <div className="lookup-field">
          <input
            value={getField('f90m_22') || ''}
            disabled
            className="field-readonly"
          />
          <span className="lookup-name">{names.position}</span>
        </div>
      </div>

      <div className="form-grid">
        <label>到職日</label>
        <input
          value={dateStr(getField('f90m_23'))}
          disabled
          className="field-readonly"
        />
        <label>入單人員</label>
        <div className="lookup-field">
          <input
            value={getField('f90m_20') || ''}
            disabled
            className="field-readonly"
          />
          <span className="lookup-name">{names.enterBy}</span>
        </div>
      </div>

      {/* Severance reason */}
      <div className="form-row">
        <label>資遣事由</label>
        <Select
          style={{ width: '100%' }}
          value={getField('f90m_13') || undefined}
          disabled={!isEditing}
          placeholder="選擇資遣事由"
          onChange={val => setField('f90m_13', val)}
          options={SEVERANCE_REASONS.map(r => ({ value: r, label: r }))}
        />
      </div>

      {/* Notes */}
      <div className="form-row">
        <label>資遣說明</label>
        <textarea
          rows={4}
          value={getField('f90m_14') || ''}
          disabled={!isEditing}
          onChange={e => setField('f90m_14', e.target.value)}
          className="form-textarea"
        />
      </div>

      {/* Financial data */}
      <div className="form-grid">
        <label>年資</label>
        <input
          type="number"
          step="0.01"
          value={getField('f90m_15') || 0}
          disabled={!isEditing}
          onChange={e => setField('f90m_15', parseFloat(e.target.value) || 0)}
          onBlur={() => autoCalcSeveranceCost(getField('f90m_15') || 0, getField('f90m_16') || 0)}
        />
        <label>本薪</label>
        <input
          type="number"
          value={getField('f90m_16') || 0}
          disabled={!isEditing}
          onChange={e => setField('f90m_16', parseFloat(e.target.value) || 0)}
          onBlur={() => autoCalcSeveranceCost(getField('f90m_15') || 0, getField('f90m_16') || 0)}
        />
      </div>

      <div className="form-grid">
        <label>資遣費用</label>
        <input
          type="number"
          value={getField('f90m_18') || 0}
          disabled={!isEditing}
          onChange={e => setField('f90m_18', parseFloat(e.target.value) || 0)}
        />
        <label>發放日期</label>
        <input
          type="date"
          value={dateStr(getField('f90m_19'))}
          disabled={!isEditing}
          onChange={e => setField('f90m_19', e.target.value || null)}
        />
      </div>
    </div>
  );
});

Tab1BasicInfo.displayName = 'Tab1BasicInfo';
export default Tab1BasicInfo;
